import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navbar as BSNavbar, Nav, Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserRole, logout } from '../../utils/auth';
import ChangePasswordModal from './ChangePasswordModal';
import NotificationModal, { transactionsToNotifications } from './NotificationModal';
import { dashboardService, Transaction } from '../../services/dashboardService';
import { cn } from '../../utils/cn';
import { BellIcon, ChevronDownIcon } from 'lucide-react';
const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = getCurrentUserRole();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [showNotificationModal, setShowNotificationModal] = useState<boolean>(false);
  const [showPaymentsPanel, setShowPaymentsPanel] = useState<boolean>(false);
  const [showProfilePanel, setShowProfilePanel] = useState<boolean>(false);
  const [notificationTransactions, setNotificationTransactions] = useState<Transaction[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const txs = await dashboardService.getTransactions(10);
      setNotificationTransactions(txs);
    } catch {
      setNotificationTransactions([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications, location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowPaymentsPanel(false);
        setShowProfilePanel(false);
        setShowNotificationModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', visible: true },
    { label: 'Users', path: '/users', visible: userRole !== 'Agent' },
    { label: 'Transactions', path: '/transactions', visible: true },
    { label: 'Settlements', path: '/settlements', visible: true },
    { label: 'Reconciliation', path: '/reconciliation', visible: true },
    {
      label: 'Setting',
      path: '/settings',
      visible: userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent',
    },
  ];

  return (
    <BSNavbar
      expand="xl"
      className="sticky top-0 z-[100] border-b border-[#e2e8f0] navbar-bg py-2.5 "
    >
      <Container fluid className="mx-auto space-x-4 w-full max-w-[1440px] h-[90px] px-4 xl:px-5">
       
       {/* <div className='w-[256px] h-[65px]'>
        <img src={'/logo.png'} alt="logo"/>
       </div> */}
       
        <BSNavbar.Brand
          onClick={() => navigate('/dashboard')}
          className="me-4 flex cursor-pointer items-center gap-3"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl  bg-white text-xs font-bold shadow-xl text-[#2297D1] shadow-sm">
           <p className="text-xs font-bold border p-1 rounded-lg border-[#2297D1] text-[#2297D1]">ZK</p>
          </div>
          <div className="leading-tight">
            <p className="mb-0 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Payment Operations
            </p>
            <p className="mb-0 text-lg font-semibold text-slate-900">ZK Gate</p>
          </div>
        </BSNavbar.Brand>

        <BSNavbar.Toggle aria-controls="top-navbar-nav" />
        <BSNavbar.Collapse
          id="top-navbar-nav"
          className="max-xl:rounded-2xl  max-xl:bg-[#F4F4F4] lg:p-0 p-2 py-4 lg:py-0 xl:rounded-2xl"
        >
          <Nav className="mx-auto items-center gap-1">
            {navItems
              .filter((item) => item.visible)
              .map((item) => (
                <Nav.Link
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'font-medium  rounded-lg px-1.5 min-[1320px]:!px-3 py-2 lg:text-base text-sm text-gray-800 transition',
                    'hover:bg-slate-100 hover:text-slate-900',
                    location.pathname === item.path && 'bg-[#E4E9EB] text-black'
                  )}
                >
                  {item.label}
                </Nav.Link>
              ))}
          </Nav>

          <div ref={actionsRef} className="relative flex items-center gap-3 lg:mt-0">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowNotificationModal((prev) => {
                    if (!prev) void loadNotifications();
                    return !prev;
                  });
                  setShowPaymentsPanel(false);
                  setShowProfilePanel(false);
                }}
                className={cn(
                  'relative flex items-center gap-2 rounded-lg bg-[#E4E9EB] p-2 text-slate-900 transition hover:bg-[#d8dee4] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/40',
                  showNotificationModal && 'ring-2 ring-[#2297D1]/40'
                )}
                aria-expanded={showNotificationModal}
                aria-label="Open notifications"
              >
                <span aria-hidden className="text-base">
                  <BellIcon />
                </span>
                <span className="inline-flex h-6 w-6 min-w-[1.5rem] items-center justify-center rounded-full bg-[#2297D1] px-1 text-xs font-semibold text-white tabular-nums">
                  {transactionsToNotifications(notificationTransactions).length}
                </span>
              </button>

              <NotificationModal
                show={showNotificationModal}
                onHide={() => setShowNotificationModal(false)}
                transactions={notificationTransactions}
                loading={loadingNotifications}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setShowPaymentsPanel((prev) => !prev);
                setShowProfilePanel(false);
                setShowNotificationModal(false);
              }}
              className={cn(
                'flex h-12 items-center rounded-lg px-7 text-4 font-medium text-white shadow-sm',
                'bg-[#2297D1] hover:bg-[#2297D1] focus:outline-none',
                showPaymentsPanel && 'ring-4 ring-[#9DD3D0]'
              )}
            >
              <span>Payments</span>
              <span className=" text-xs" aria-hidden><ChevronDownIcon /></span>
            </button>

            <button
              type="button"
              onClick={() => {
                setShowProfilePanel((prev) => !prev);
                setShowPaymentsPanel(false);
                setShowNotificationModal(false);
              }}
              className={cn(
                'flex h-12 items-center rounded-lg bg-[#E4E9EB] px-7 text-4 font-medium text-slate-900 shadow-sm',
                'focus:outline-none',
                showProfilePanel && 'ring-2 ring-[#2297D1]/40'
              )}
            >
              <span>Profile</span>
              <span className=" text-xs" aria-hidden><ChevronDownIcon /></span>
            </button>

            {showPaymentsPanel && (
              <div className="absolute right-[178px] top-[64px] z-50 w-[400px] z-50 rounded-[30px] border border-[#d7dde1] bg-[#F4F4F4] p-8 shadow-[0_20px_40px_rgba(15,23,42,0.15)]">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentsPanel(false);
                    navigate('/payments/new');
                  }}
                  className="w-full text-left"
                >
                  <p className="mb-2 lg:text-xl leading-none font-semibold text-[#111]">New payment</p>
                  <p className="mb-0 leading-none text-sm text-[#5f6368]">
                    Create a fresh payment link for direct crypto or swap checkout.
                  </p>
                </button>
                <div className="my-8 h-px bg-[#e3e7ea]" />
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentsPanel(false);
                    navigate('/payments/invoice');
                  }}
                  className="w-full text-left"
                >
                  <p className="mb-2 leading-none text-xl font-semibold text-[#111]">New invoice</p>
                  <p className="mb-0 leading-none text-sm text-[#5f6368]">
                    Issue an invoice-style payment request without leaving the current page.
                  </p>
                </button>
              </div>
            )}

            {showProfilePanel && (
              <div className="absolute right-0 top-[64px] z-50 w-[400px] z-50 rounded-[30px] border border-[#d7dde1] bg-[#F4F4F4] p-8 shadow-[0_20px_40px_rgba(15,23,42,0.15)]">
                <button
                  type="button"
                  onClick={() => {
                    setShowProfilePanel(false);
                    handleChangePassword();
                  }}
                  className="w-full text-left"
                >
                  <p className="mb-2 leading-none text-xl font-semibold text-[#111]">Change password</p>
                  <p className="mb-0 leading-none text-sm text-[#5f6368]">
                    Update your login credentials without leaving the workspace.
                  </p>
                </button>
                <div className="my-8 h-px bg-[#e3e7ea]" />
                <button
                  type="button"
                  onClick={() => {
                    setShowProfilePanel(false);
                    handleLogout();
                  }}
                  className="w-full text-left"
                >
                  <p className="mb-2 leading-none text-xl font-semibold text-[#111]">Logout</p>
                  <p className="mb-0 leading-none text-sm text-[#5f6368]">
                    End this session and return to the sign-in screen.
                  </p>
                </button>
              </div>
            )}
            </div>
        </BSNavbar.Collapse>
      </Container>

      <ChangePasswordModal
        show={showChangePasswordModal}
        onHide={() => setShowChangePasswordModal(false)}
      />
    </BSNavbar>
  );
};

export default Navbar;
